import { useEffect, useRef } from 'react';
import { useCurrentUser } from '../contexts/AuthContext';

// 15 minutes of inactivity in milliseconds
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
// Throttle DOM events to max once per second to preserve performance
const THROTTLE_MS = 1000;

export const IdleTimeoutHandler = () => {
  const { isSignedIn, signOut } = useCurrentUser();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(0);

  useEffect(() => {
    if (!isSignedIn) return;

    // Seed activity time inside the effect (Date.now() is not allowed during render).
    lastActivityRef.current = Date.now();

    const handleIdle = () => {
      sessionStorage.setItem('logout_reason', 'idle');
      signOut();
    };

    const resetTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleIdle, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > THROTTLE_MS) {
        lastActivityRef.current = now;
        resetTimer();
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    resetTimer();
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, [isSignedIn, signOut]);

  return null;
};